import { useEffect, useRef, useState } from 'react';
import { ssrDataFor } from '../utils/ssrPage';

export interface ShareData<T> {
    data: T | null;
    loading: boolean;
    failed: boolean;
}

interface State<T> extends ShareData<T> {
    path: string;
}

// Loads a public share page's data: from the copy the server embedded (utils/ssrPage.ts) when
// this is the server-rendered page — no fetch, no "Loading…" — otherwise with `load`. Changing
// `path` resets the state and ignores the previous path's late responses. Under StrictMode the
// effect runs twice; both runs see the embedded copy, so neither fetches.
export function useShareData<T, E = T>(
    path: string,
    load: () => Promise<T>,
    fromEmbedded: (embedded: E) => T = (e) => e as unknown as T,
): ShareData<T> {
    const loadRef = useRef(load);
    const fromEmbeddedRef = useRef(fromEmbedded);
    loadRef.current = load;
    fromEmbeddedRef.current = fromEmbedded;

    const [state, setState] = useState<State<T>>(() => {
        const e = ssrDataFor<E>(path);
        const data = e === null ? null : fromEmbedded(e);
        return { path, data, loading: data === null, failed: false };
    });

    useEffect(() => {
        const e = ssrDataFor<E>(path);
        if (e !== null) {
            setState((s) => (s.path === path && s.data !== null ? s : { path, data: fromEmbeddedRef.current(e), loading: false, failed: false }));
            return;
        }
        let cancelled = false;
        setState({ path, data: null, loading: true, failed: false });
        loadRef.current()
            .then((data) => {
                if (!cancelled) setState({ path, data, loading: false, failed: false });
            })
            .catch(() => {
                if (!cancelled) setState({ path, data: null, loading: false, failed: true });
            });
        return () => {
            cancelled = true;
        };
    }, [path]);

    // Until the effect for a new path has run, don't show the previous path's data.
    if (state.path !== path) return { data: null, loading: true, failed: false };
    return { data: state.data, loading: state.loading, failed: state.failed };
}
